import MinusSVG from '@public/icons/minus.svg';
import PlusSVG from '@public/icons/plus.svg';
import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { PAGES } from '@/consts';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import Button from '@/shared/components/shared/Button';
import Image from '@/shared/components/shared/Image';
import { scrollToAnchor } from '@/shared/lib/utils/scrollToAnchor';
import Typografed from '../../shared/Typografed';
import MapDescription from '../MapDescription';
import MapPavilion from '../MapPavilion';
import MapSectionRoomsWrapper from '../MapSectionRoomsWrapper';
import { usePavilionsValue, useSetPavilionsState } from '../atoms/pavilionsData';
import getStandsDataMap from '../utils/getStandsDataMap';
import { useActiveRoom, useSetActiveRoom } from '../utils/use-active-room';
import { useMapDrag } from './hooks/useMapDrag';
import { useMapLayout } from './hooks/useMapLayout';
import { useMapScrollToRoom } from './hooks/useMapScrollToRoom';
import { useMapZoom } from './hooks/useMapZoom';

export const SECTION_ID = 'map-section';
const MAP_SLUGS = ['map-for-visitors-2026', 'karta-posetiteliam-ulica-26'];
// const MAP_SLUGS = ['map-for-visitors-2026'];
const PARTICIPANTS_LIST_ANCHOR = 'tarifs';

export type MapSectionProps = {
    title?: string | null;
    description?: string | null;
    buttonText?: string | null;
};

type Props = Omit<React.HTMLAttributes<HTMLElement>, 'title'> & MapSectionProps;

const MapSection = ({ title, description, buttonText, ...props }: Props) => {
    const setPavilions = useSetPavilionsState();
    const [viewMode, setViewMode] = useState<'pavilion' | 'street'>('pavilion');
    const [streetImageLoaded, setStreetImageLoaded] = useState(false);

    useEffect(() => {
        getStandsDataMap(MAP_SLUGS, PAGES.VISITORS).then((result) => {
            setPavilions(result);
        });
    }, [setPavilions]);

    const { pavilionData } = usePavilionsValue();
    const streetImage = pavilionData.images?.[MAP_SLUGS[1]];
    const setActiveRoomInStore = useSetActiveRoom();
    const activeRoom = useActiveRoom();

    const device = useMediaQueryDeviceStateValue();
    const isMobile = device.includes('mobile');
    const isDesktop = device.includes('desktop');

    const { mapPlanRef, isDragging, onMouseDown } = useMapDrag({ isMobile, isDesktop });
    const { handleZoom, setZoomLevel } = useMapZoom({ mapPlanRef, isMobile, activeRoom });

    const handleResize = useCallback(() => setZoomLevel(1), [setZoomLevel]);

    useMapLayout(mapPlanRef, handleResize, `${viewMode}-${streetImageLoaded}`);
    useMapScrollToRoom(mapPlanRef, activeRoom);

    const handlePavilionClick = () => {
        setZoomLevel(1);
        setViewMode('pavilion');
        setActiveRoomInStore(null);
    };

    const handleStreetClick = () => {
        setZoomLevel(1);
        setViewMode('street');
        setActiveRoomInStore(null);
    };

    const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        scrollToAnchor(PARTICIPANTS_LIST_ANCHOR);
    };

    const sectionTitle = title || 'Схема выставки';

    return (
        <section
            {...props}
            id={props.id || SECTION_ID}
            className={classNames('map__section js-map__section', props.className, {
                'is-street': viewMode === 'street',
                'is-pavilion': viewMode === 'pavilion',
                'has-active-room': Boolean(activeRoom),
            })}
        >
            {pavilionData && (
                <div className="map__section-wrapper wrapper">
                    <div className="map__section-wrapper__header">
                        <div className="map__section-wrapper__intro">
                            <h2 className="map__section-wrapper__title h3">
                                <Typografed>{sectionTitle}</Typografed>
                            </h2>
                            {description && (
                                <div className="map__section-wrapper__description-wrapper">
                                    <p className="map__section-wrapper__description text-s">
                                        <Typografed>{description}</Typografed>
                                    </p>
                                </div>
                            )}
                            {buttonText && (
                                <Button
                                    tag="a"
                                    href={`#${PARTICIPANTS_LIST_ANCHOR}`}
                                    variant="outline-dark"
                                    className="map__section-wrapper__button text-xs"
                                    onClick={handleButtonClick}
                                >
                                    <Typografed>{buttonText}</Typografed>
                                </Button>
                            )}
                        </div>
                        <div className="map__togler-wrapper">
                            <div className="map__togler text-xs">
                                <button
                                    className={classNames('map__togler--pavilion', {
                                        active: viewMode === 'pavilion',
                                    })}
                                    onClick={handlePavilionClick}
                                >
                                    Павильон
                                </button>
                                <button
                                    className={classNames('map__togler--street', {
                                        active: viewMode === 'street',
                                    })}
                                    onClick={handleStreetClick}
                                >
                                    Улица
                                </button>
                            </div>

                            {isMobile && (
                                <div className="map__plan-controls">
                                    <button
                                        className="map__plan-control"
                                        aria-label="Приблизить"
                                        onClick={handleZoom('in')}
                                    >
                                        <PlusSVG className="map-svg__zoom" />
                                    </button>
                                    <button
                                        className="map__plan-control"
                                        aria-label="Отдалить"
                                        onClick={handleZoom('out')}
                                    >
                                        <MinusSVG className="map-svg__zoom" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="map__wrapper">
                        <div
                            className={classNames('map', {
                                'map__container--full': viewMode === 'street' || !activeRoom,
                            })}
                        >
                            <div className="map__container">
                                {!isMobile && (
                                    <div className="map__plan-controls">
                                        <button
                                            className="map__plan-control"
                                            aria-label="Приблизить"
                                            onClick={handleZoom('in')}
                                        >
                                            <PlusSVG className="map-svg__zoom" />
                                        </button>
                                        <button
                                            className="map__plan-control"
                                            aria-label="Отдалить"
                                            onClick={handleZoom('out')}
                                        >
                                            <MinusSVG className="map-svg__zoom" />
                                        </button>
                                    </div>
                                )}
                                <div
                                    className={classNames('map__plan js-map__plan', {
                                        'map__plan--dragging': isDragging,
                                    })}
                                    ref={mapPlanRef}
                                    onMouseDown={onMouseDown}
                                >
                                    {viewMode === 'street' ? (
                                        streetImage?.src ? (
                                            <div className="map-section-wrapper-item">
                                                <Image
                                                    src={streetImage.src}
                                                    alt={streetImage.alt || ''}
                                                    width={streetImage.width}
                                                    height={streetImage.height}
                                                    title={streetImage.title}
                                                    className="map__rooms js-map__rooms map__plan-image"
                                                    onLoad={() => setStreetImageLoaded(true)}
                                                />
                                            </div>
                                        ) : null
                                    ) : (
                                        <MapSectionRoomsWrapper className="map__rooms--pavilion" slug={MAP_SLUGS[0]}>
                                            <MapPavilion />
                                        </MapSectionRoomsWrapper>
                                    )}
                                </div>
                            </div>
                        </div>
                        {viewMode === 'pavilion' && (
                            <div
                                className={classNames('map-description', {
                                    'map-description-close': !activeRoom,
                                })}
                            >
                                <MapDescription slug={MAP_SLUGS[0]} />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

export default MapSection;
